import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { ILead } from '@clickersphere/core/entities/ILead';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { LeadSourceEnum } from '@clickersphere/core/enums/LeadSourceEnum';
import { useAttachBadge } from '../../../../app/hooks/badge/useAttachBadge';
import { useDetachBadge } from '../../../../app/hooks/badge/useDetachBadge';
import { useCreateLead } from '../../../../app/hooks/lead/useCreateLead';
import { useUpdateLead } from '../../../../app/hooks/lead/useUpdateLead';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

interface IUseRegisterLeadForm {
  lead?: ILead;
}

export function useRegisterLeadFormController({ lead }: IUseRegisterLeadForm) {
  const [selectedReferrerName, setSelectedReferrerName] = useState<
    string | undefined
  >(lead?.referrerName ?? undefined);
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { toast } = useToast();
  const { handleError } = useError();
  const { createLeadAsync, createLeadIsPending } = useCreateLead();
  const { updateLeadAsync, updateLeadIsPending } = useUpdateLead();
  const { attachBadgeAsync } = useAttachBadge();
  const { detachBadgeAsync } = useDetachBadge();

  const schema = z.object({
    name: z
      .string()
      .nonempty(t('register.errors.name.required'))
      .refine((value) => /^[A-Za-z\s]+$/.test(value), {
        message: t('register.errors.name.invalid'),
      }),
    email: z
      .email(t('register.errors.email.invalid'))
      .nonempty(t('register.errors.email.required')),
    phone: z
      .string()
      .max(30, t('register.errors.phone.invalid'))
      .transform((value) => (value === '' ? undefined : value))
      .optional(),
    instagram: z
      .string()
      .max(30, t('register.errors.instagram.invalid'))
      .refine(
        (value) => {
          if (value) {
            return /^[a-zA-Z0-9](?:[a-zA-Z0-9._]{0,29})$/.test(value);
          } else {
            return true;
          }
        },
        {
          message: t('register.errors.instagram.invalidUserName'),
        },
      )
      .transform((value) => (value === '' ? undefined : value))
      .optional(),
    notes: z
      .string()
      .max(300, t('register.errors.notes.invalid'))
      .transform((value) => (value === '' ? undefined : value))
      .optional(),
    source: z.string().optional(),
    referrerId: z.string().optional(),
    badgeIds: z.array(z.string()).default([]),
  });

  type FormData = z.input<typeof schema>;

  const initialBadgeIds = lead?.badges?.map((b) => b.id) ?? [];

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema) as never,
    defaultValues: {
      ...lead,
      source: lead?.source ?? '',
      referrerId: lead?.referrerId ?? '',
      badgeIds: initialBadgeIds,
    },
  });

  const selectedBadgeIds = watch('badgeIds') ?? [];

  function handleBadgeIdsChange(ids: string[]) {
    setValue('badgeIds', ids, { shouldDirty: true });
  }

  function handleFormReset() {
    reset();
    setSelectedReferrerName(lead?.referrerName ?? undefined);
  }

  async function syncBadges(
    leadId: string,
    nextIds: string[],
  ): Promise<number> {
    const previous = new Set(initialBadgeIds);
    const next = new Set(nextIds);

    const toAttach = nextIds.filter((id) => !previous.has(id));
    const toDetach = initialBadgeIds.filter((id) => !next.has(id));

    const results = await Promise.allSettled([
      ...toAttach.map((badgeId) => attachBadgeAsync({ leadId, badgeId })),
      ...toDetach.map((badgeId) => detachBadgeAsync({ leadId, badgeId })),
    ]);

    return results.filter((r) => r.status === 'rejected').length;
  }

  async function attachInitialBadges(
    leadId: string,
    badgeIds: string[],
  ): Promise<number> {
    if (badgeIds.length === 0) return 0;
    const results = await Promise.allSettled(
      badgeIds.map((badgeId) => attachBadgeAsync({ leadId, badgeId })),
    );
    return results.filter((r) => r.status === 'rejected').length;
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const badgeIds = data.badgeIds ?? [];
    const leadPayload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      instagram: data.instagram,
      notes: data.notes,
      source: data.source ? (data.source as LeadSourceEnum) : undefined,
      referrerId: data.referrerId || null,
    };
    try {
      let badgeFailures = 0;
      if (!lead) {
        const created = await createLeadAsync({
          ...leadPayload,
          referrerId: leadPayload.referrerId ?? undefined,
        });
        badgeFailures = await attachInitialBadges(created.id, badgeIds);
        toast(t('register.toast.lead.created'), 'success');
      } else {
        await updateLeadAsync({
          id: lead.id,
          data: leadPayload,
          optimisticFields: {
            referrerName: leadPayload.referrerId
              ? (selectedReferrerName ?? null)
              : null,
          },
        });
        badgeFailures = await syncBadges(lead.id, badgeIds);
        toast(t('register.toast.lead.updated'), 'success');
      }

      if (badgeFailures > 0) {
        toast(
          t('register.toast.lead.partialBadges', { count: badgeFailures }),
          'error',
        );
        return;
      }

      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('register.toast.lead.error'), 'error');
      }
    }
  });

  return {
    isFetching: createLeadIsPending || updateLeadIsPending,
    errors,
    control,
    handleSubmit,
    register,
    handleFormReset,
    selectedBadgeIds,
    handleBadgeIdsChange,
    setSelectedReferrerName,
  };
}
